<template>
  <v-card class="px-5" style="height: 100%" flat="flat">
    <div class="d-flex flex-column justify-space-between" style="height: 100%">
      <div class="d-flex align-center">
        <img-icon :icon="graphIcon"></img-icon>
        <div>
          <v-card-title>{{ graphTitle }}</v-card-title>
          <v-card-subtitle :class="greyTextColor"> {{ graphDescription }}</v-card-subtitle>
        </div>
      </div>
      <div class="d-flex">
        <div ref="chart" class="flex-grow-1"></div>
        <div v-if="checkApexDataLength" class="graph-text d-flex flex-column justify-center align-center around">
          <p class="font-weight-medium" :style="{ color: valueTextColor }">{{ getYValue }}<span v-if="isPercentage">%</span></p>
          <div class="d-flex flex-column align-center mt-10">
            <p class="font-weight-medium" :class="greyTextColor">{{ getYAverage }}<span v-if="isPercentage">%</span></p>
            <p class="text-center caption" :class="greyTextColor">⌀ {{ $t('telemetry.all_drivers') }}</p>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import ApexCharts from 'apexcharts';
import ImgIcon from '@/components/icons/ImgIcon.vue';
import common from '@/mixins/common';

/**
 * @typedef {Object} ParameterRanges
 * @property {number} min - Minimum value.
 * @property {number} max - Maximum value.
 * @property {number} minOk - Minimum OK value.
 * @property {number} maxOk - Maximum OK value.
 * @property {number} minWarning - Minimum warning value.
 * @property {number} maxWarning - Maximum warning value.
 * @property {number} minError - Minimum error value.
 * @property {number} maxError - Maximum error value.
 */

export default {
  name: 'TelemetrieChart',
  components: { ImgIcon },
  mixins: [common],
  props: {
    // Chart Props
    chartData: {
      type: Array,
      required: true,
    },
    chartOptions: {
      type: Object,
      required: true,
    },
    graphType: {
      type: String,
      required: true,
    },
    graphIcon: {
      type: String,
      required: true,
    },
    graphTitle: {
      type: String,
      required: true,
    },
    graphDescription: {
      type: String,
      default: '',
    },
    /**
     * @type {ParameterRanges}
     */
    parameterRanges: {
      type: Object,
      required: true,
    },
    isPercentage: {
      type: Number || Boolean,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      chart: null,
      valueTextColor: '',
    };
  },
  mounted() {
    this.chart = new ApexCharts(this.$refs.chart, this.getChartOptions());
    this.chart.render();
  },
  beforeDestroy() {
    if(!this.chart){
      return;
    }
    this.chart.destroy()
  },
  methods: {
    updateChart() {
      if (this.getChartOptions()) {
        setTimeout(() => {
          this.chart.updateOptions(this.getChartOptions());
        }, Math.random() * 1000);
      }
    },
    getChartOptions() {
      return {
        ...this.chartOptions,
        chart: {
          ...this.chartOptions.chart,
          type: this.graphType,
        },
        series: this.chartData,
      };
    },
    setColorForValue(value) {
      let color;
      const isValueBetweenOkMinMax = value >= this.parameterRanges.minOk && value <= this.parameterRanges.maxOk;
      const isValueBetweenWarningMinMax = value >= this.parameterRanges.minWarning && value <= this.parameterRanges.maxWarning;
      const isValueBetweenErrorMinMax = value >= this.parameterRanges.minError && value <= this.parameterRanges.maxError;
      if (value === null) {
        color = '';
      } else if (isValueBetweenOkMinMax) {
        color = this.themeName === 'dark' ? '#99ea8b' : '#04850a'; // OK
      } else if (isValueBetweenWarningMinMax) {
        color = this.themeName === 'dark' ? '#FFA126' : '#d27e00'; // Warnung
      } else if (isValueBetweenErrorMinMax) {
        color = this.themeName === 'dark' ? '#FF4B4B' : '#e20000'; // Fehler
      } else {
        color = '';
      }
      this.valueTextColor = color;
    },
  },
  computed: {
    checkApexDataLength() {
      return !(this.chartData && this.chartData[0] && this.chartData[0].data && this.chartData[0].data.length > 1);
    },
    getYValue() {
      const chartDataPoint = this.chartData[0].data[0];
      if (chartDataPoint) {
        if (chartDataPoint.y || chartDataPoint.y === 0) {
          this.setColorForValue(chartDataPoint.y);
          return chartDataPoint.y;
        }
      }
      this.setColorForValue(null);
      return '-';
    },
    getYAverage() {
      const chartDataPoint = this.chartData[1].data[0];
      if (chartDataPoint) {
        if (chartDataPoint.y || chartDataPoint.y === 0) {
          return chartDataPoint.y;
        }
      }
      return '-';
    },
    greyTextColor() {
      return 'text-grey--' + this.themeName;
    },
  },
  watch: {
    chartData: { handler: 'updateChart' },
    chartOptions: { handler: 'updateChart' },
  },
};
</script>

<style scoped lang="scss">
.graph-text p {
  margin-bottom: 0;
}

.text-grey--light {
  color: #5e5e5e;
}
.text-grey--dark {
  color: #a1a1a1;
}
</style>